import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTableConfiguration, useTableRecords } from "./table-manager-hooks";

const TableManagerContext = createContext({});

const { Provider } = TableManagerContext;

const normalizeFilterList = (filters) =>
  Array.isArray(filters) ? filters : [];

const toActiveFilterArray = (activeFilters = {}) => {
  const normalizedActiveFilters =
    activeFilters && typeof activeFilters === "object" ? activeFilters : {};

  return [
    ...normalizeFilterList(normalizedActiveFilters.quick_filters),
    ...normalizeFilterList(normalizedActiveFilters.comprehensive_filters),
  ].map(({ column, value, filterValue }) => ({
    column,
    filterValue: value !== undefined ? value : filterValue,
  }));
};

const toFilterColumnMap = (filters = []) =>
  filters.reduce((map, filter) => {
    map[filter.column] = filter;
    return map;
  }, {});

const isAllOption = ({ label }) =>
  String(label || "")
    .toLowerCase()
    .includes("all");

const isInitiallyActive = (option) => option.initially_active;

const buildQuickDefaultFilters = (
  quickFilters = [],
  activeFilterByColumn = {},
) =>
  normalizeFilterList(quickFilters).reduce(
    (filters, { column, options = [] }) => {
      if (activeFilterByColumn[column]) return filters;

      const defaultOption = options.find(isInitiallyActive);
      if (!defaultOption || isAllOption(defaultOption)) return filters;

      filters.push({
        column,
        filterValue: defaultOption.value,
      });
      return filters;
    },
    [],
  );

const buildComprehensiveDefaultFilters = (
  comprehensiveFilters = [],
  activeFilterByColumn = {},
) =>
  normalizeFilterList(comprehensiveFilters).reduce(
    (filters, { column, type, options = [] }) => {
      if (activeFilterByColumn[column]) return filters;

      const defaultOptions = options.filter(isInitiallyActive);
      if (defaultOptions.length === 0) return filters;

      filters.push({
        column,
        filterValue:
          type === "multiple"
            ? defaultOptions.map((option) => option.value)
            : defaultOptions[0].value,
      });
      return filters;
    },
    [],
  );

const buildConfiguredInitialFilters = (filterConfig = {}) => {
  const activeFilters = toActiveFilterArray(filterConfig?.active_filters || {});
  const activeFilterByColumn = toFilterColumnMap(activeFilters);

  return [
    ...Object.values(activeFilterByColumn),
    ...buildQuickDefaultFilters(
      filterConfig?.quick_filters || [],
      activeFilterByColumn,
    ),
    ...buildComprehensiveDefaultFilters(
      filterConfig?.comprehensive_filters || [],
      activeFilterByColumn,
    ),
  ];
};

const TableManagerContextProvider = ({
  children,
  url,
  reqMethod = "POST",
  directRecords,
  propTotal,
  refreshKey,
  columns,
  filterConfig,
  selectable,
  recordsToExclue,
  uniqueAccessor,
  onSelectRecordCallback,
  bulkOptionsConfig,
  actionsConfig,
  searchNeeded = true,
  printNeeded = true,
  exportNeeded = true,
  columChooseNeeded = true,
  setTotal,
  setResult,
  setSelectedRows,
  ExtraComponent,
  extraComponentProperties,
  setIsRestricted,
  isPayment,
  clearSelectedRowsRef,
  columnResizeNeeded,
  visitedRecords,
  setVisitedRecords,
  searchControlNeeded,
  clientSide = false,
  paginationNeeded = true,
  defaultPageSize = 10, // 1. Catch the prop here, default to 10
  setApiColumns,
  setApiFilterConfig,
  apiClient,
}) => {
  const [columnsToDisplay, setColumnsToDisplay] = useState([]);
  const [insertColumn, setInsertColumn] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const activeFilterAppliedRef = useRef(false);

  useEffect(() => {
    if (insertColumn === null) return;
    setColumnsToDisplay((prevColumns) => {
      const newColumns = [...prevColumns];
      const draggedColumn = prevColumns[insertColumn.columnIndex];
      newColumns.splice(insertColumn.columnIndex, 1);
      newColumns.splice(insertColumn.insertBefore, 0, draggedColumn);
      return newColumns;
    });
  }, [insertColumn]);

  const configuredInitialFilter = useMemo(() => {
    return buildConfiguredInitialFilters(filterConfig);
  }, [
    filterConfig?.active_filters,
    filterConfig?.quick_filters,
    filterConfig?.comprehensive_filters,
  ]);

  const {
    tableConfig,
    filter,
    setFilter,
    handleTableConfigSearchChange,
    handleTableConfigSortChange,
  } = useTableConfiguration(configuredInitialFilter);

  const activeFiltersKey = filterConfig?.activeFiltersKey;

  useEffect(() => {
    if (!activeFilterAppliedRef.current) {
      activeFilterAppliedRef.current = true;
      return;
    }
    if (activeFiltersKey === undefined) return;

    const activeFilterArray = configuredInitialFilter;
    if (activeFilterArray.length === 0) return;

    setFilter(activeFilterArray);
  }, [activeFiltersKey, configuredInitialFilter, setFilter]);

  // Build a value→label lookup map per column from filterConfig options.
  // Used by client-side search to match against the display label instead
  // of the raw record value.
  //
  // Shape: { [column]: { [value]: label } }
  // Example: { connectionType: { "QUICK_SUPPORT": "Quick Connect", ... } }
  const valueLabelMap = useMemo(() => {
    if (!filterConfig) return {};
    const allFilters = [
      ...(filterConfig.quick_filters || []),
      ...(filterConfig.comprehensive_filters || []),
    ];
    return allFilters.reduce((map, { column, options }) => {
      if (!options) return map;
      map[column] = options.reduce((colMap, { value, label }) => {
        colMap[value] = label;
        return colMap;
      }, {});
      return map;
    }, {});
  }, [filterConfig]);

  const { records, pagination, setPagination } = useTableRecords(
    url,
    reqMethod,
    directRecords,
    propTotal,
    recordsToExclue,
    uniqueAccessor,
    tableConfig,
    clientSide,
    valueLabelMap,
    refreshKey,
    setTotal,
    setResult,
    setIsRestricted,
    isPayment,
    visitedRecords,
    setVisitedRecords,
    defaultPageSize,
    setApiColumns,
    setApiFilterConfig,
    apiClient,
    paginationNeeded,
  );

  const [tableControls, setTableControls] = useState({
    selectable: selectable || false,
    sortable: true,
    searchable: false,
    print: false,
  });

  const [tableControlsVisibility] = useState({
    searchNeeded,
    printNeeded,
    exportNeeded,
    columChooseNeeded,
  });

  useEffect(() => {
    if (onSelectRecordCallback) {
      onSelectRecordCallback({
        selected: [...selectedRecords],
        total: records.length,
      });
    }
  }, [selectedRecords]);

  return (
    <Provider
      value={{
        url,
        tableControls,
        setTableControls,
        records,
        columns,
        columnsToDisplay,
        setColumnsToDisplay,
        setInsertColumn,
        bulkOptionsConfig,
        actionsConfig,
        filter,
        setFilter,
        filterConfig,
        selectOptions: {
          selectable: tableControls.selectable,
          selectedRecords,
          setSelectedRecords,
        },
        sortOptions: {
          onSortChange: handleTableConfigSortChange,
          sortable: tableControls.sortable,
          config: tableConfig.sort,
        },
        searchOptions: {
          onSearchChange: handleTableConfigSearchChange,
          searchable: tableControls.searchable,
          searchConfig: tableConfig.search,
        },
        pagination,
        setPagination,
        tableControlsVisibility,
        ExtraComponent,
        extraComponentProperties,
        clearSelectedRowsRef,
        columnResizeNeeded,
        setSelectedRows,
        visitedRecords,
        setVisitedRecords,
        searchControlNeeded,
        paginationNeeded,
      }}
    >
      {children}
    </Provider>
  );
};

const useTableManagerContext = () => {
  return useContext(TableManagerContext);
};

export { TableManagerContextProvider, useTableManagerContext };

export default TableManagerContext;
